import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "tableBody", "tableContainer", "sentinel", "empty", "skeleton",
    "searchInput", "typeCheckbox", "typeToggleAll", "typeBadge",
    "dateFrom", "dateTo", "dateLabel", "datePresets",
    "filterBadge", "clearFiltersRow",
    "viewSwitcher", "detailModal", "detailBackdrop", "detailPanel", "detailContent",
    "detailTitle", "editLink", "exportPdfLink", "deleteBtn", "deleteConfirmModal", "prevBtn", "nextBtn",
    "publishBtn", "publishLabel", "publishIconCheck", "publishIconX", "releaseEditBtn", "distributeBtn",
    "distributeModal", "distributeBody", "distributeSelectAll", "distributeSendBtn",
    "calendarContainer", "calendarTitle", "calendarStats", "calendarGrid", "calendarPopover",
    "popoverTitle", "popoverBody",
    "exportAllBtn",
    "sortIconDate", "sortIconType", "sortIconNotes", "sortIconAuthor"
  ]

  static values = {
    // The table's columns in order (Studio can change them, 2026-10-17); the
    // page prints them from Studio::TableColumnsChange.
    columns: Array,
    projectId: Number,
    reportsUrl: String,
    paperclipHtml: String,
    calendarUrl: String,
    saveViewModeUrl: String,
    saveFiltersUrl: String,
    savedFilters: { type: Object, default: {} },
    currentView: { type: String, default: "table" },
    generatingRowText: { type: String, default: "Generating report..." }
  }

  connect() {
    // Studio changed this table's columns (step 2, 2026-10-19): the rows redraw in the new order, no reload.
    this._onStudioColumns = () => { try { this._reRenderTable() } catch (e) { /* the table draws on the next load */ } }
    window.addEventListener("studio:table-columns", this._onStudioColumns)
    this._page = 1
    this._loading = false
    this._isLastPage = false
    this._searchTimeout = null
    this._rowIndex = 0
    this._filters = {}
    this._reportIds = []       // ordered list of loaded report IDs
    this._reportsMap = {}      // id → report data (for RSO pdf_url etc.)
    this._sort = { key: null, order: null }
    this._activeReportId = null
    this._modalOpen = false
    this._onKeydown = this._onKeydown.bind(this)
    this._onClickOutsidePopover = this._onClickOutsidePopover.bind(this)
    this._onCustomerReportCreated = this._onCustomerReportCreated.bind(this)
    this._onCustomerReportReady = this._onCustomerReportReady.bind(this)
    this._onSpecialReportCreated = this._onSpecialReportCreated.bind(this)
    this._onSpecialReportReady = this._onSpecialReportReady.bind(this)

    // Calendar state
    const now = new Date()
    this._calMonth = now.getMonth() + 1 // 1-based
    this._calYear = now.getFullYear()
    this._calData = null
    this._calLoading = false

    this._observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this._loading && !this._isLastPage) {
          this._loadPage(this._page)
        }
      },
      { rootMargin: "300px", threshold: 0 }
    )

    // Restore saved filters before loading data
    this._restoreSavedFilters()

    // Restore saved view mode
    if (this.currentViewValue === "calendar") {
      this._initCalendarView()
    } else {
      this._loadPage(1)
    }

    // Auto-open modal if URL contains a report ID (deep link)
    this._checkDeepLink()

    // Listen for customer report creation/completion events
    document.addEventListener("customer-report:created", this._onCustomerReportCreated)
    document.addEventListener("customer-report:ready", this._onCustomerReportReady)
    document.addEventListener("special-report:created", this._onSpecialReportCreated)
    document.addEventListener("special-report:ready", this._onSpecialReportReady)
  }

  disconnect() {
    window.removeEventListener("studio:table-columns", this._onStudioColumns)
    if (this._observer) this._observer.disconnect()
    document.removeEventListener("keydown", this._onKeydown)
    document.removeEventListener("click", this._onClickOutsidePopover)
    document.removeEventListener("customer-report:created", this._onCustomerReportCreated)
    document.removeEventListener("customer-report:ready", this._onCustomerReportReady)
    document.removeEventListener("special-report:created", this._onSpecialReportCreated)
    document.removeEventListener("special-report:ready", this._onSpecialReportReady)
  }

  // ── Column Sorting ──────────────────────────────────────────────

  sortColumn(event) {
    const key = event.currentTarget.dataset.sortKey
    if (this._sort.key === key) {
      this._sort.order = this._sort.order === "asc" ? "desc" : "asc"
    } else {
      this._sort.key = key
      this._sort.order = "asc"
    }
    this._reRenderTable()
  }

  _reRenderTable() {
    this._rowIndex = 0
    const reports = this._reportIds.map(id => this._reportsMap[id]).filter(Boolean)
    const sorted = this._sortData(reports)
    this.tableBodyTarget.innerHTML = sorted.map(r => this._renderRow(r)).join("")
    this._updateSortIcons()
  }

  _sortData(items) {
    if (!this._sort.key) return items
    const sorted = [...items]
    const dir = this._sort.order === "desc" ? -1 : 1
    sorted.sort((a, b) => {
      let va, vb
      switch (this._sort.key) {
        case "date":
          va = a.date || ""; vb = b.date || ""
          return va < vb ? -dir : va > vb ? dir : 0
        case "type":
          va = (a.type_label || a.type_name || "").toLowerCase()
          vb = (b.type_label || b.type_name || "").toLowerCase()
          return va < vb ? -dir : va > vb ? dir : 0
        case "notes":
          va = (a.narration || "").toLowerCase()
          vb = (b.narration || "").toLowerCase()
          return va < vb ? -dir : va > vb ? dir : 0
        case "author":
          va = (a.author_name || "").toLowerCase()
          vb = (b.author_name || "").toLowerCase()
          return va < vb ? -dir : va > vb ? dir : 0
        default: return 0
      }
    })
    return sorted
  }

  _updateSortIcons() {
    const arrowUp = `<svg class="w-3.5 h-3.5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5"/></svg>`
    const arrowDown = `<svg class="w-3.5 h-3.5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/></svg>`
    const keys = { date: "Date", type: "Type", notes: "Notes", author: "Author" }
    for (const [key, name] of Object.entries(keys)) {
      const targetName = `sortIcon${name}Target`
      const hasName = `hasSortIcon${name}Target`
      if (this[hasName]) {
        this[targetName].innerHTML = this._sort.key === key ? (this._sort.order === "asc" ? arrowUp : arrowDown) : ""
      }
    }
  }

  // ── Search (300ms debounce) ────────────────────────────────────────

  search() {
    clearTimeout(this._searchTimeout)
    this._searchTimeout = setTimeout(() => {
      this._filters.search = this.searchInputTarget.value.trim()
      this._resetAndLoad()
    }, 300)
  }

  // ── Filters ────────────────────────────────────────────────────────

  filterTypes() {
    const checkboxes = this.typeCheckboxTargets
    const checked = checkboxes.filter(cb => cb.checked)
    const toggle = this.typeToggleAllTarget

    if (checked.length === checkboxes.length) {
      toggle.checked = true
      toggle.indeterminate = false
    } else if (checked.length === 0) {
      toggle.checked = false
      toggle.indeterminate = false
    } else {
      toggle.checked = false
      toggle.indeterminate = true
    }

    this._updateTypeBadge()
    this._updateFilterBadge()
    this._applyTypeFilter()
    this._persistFilters()
  }

  toggleAllTypes(event) {
    const state = event.currentTarget.checked
    this.typeCheckboxTargets.forEach(cb => cb.checked = state)
    event.currentTarget.indeterminate = false
    this._updateTypeBadge()
    this._updateFilterBadge()
    this._applyTypeFilter()
    this._persistFilters()
  }

  filterByDate() {
    const from = this.hasDateFromTarget ? this.dateFromTarget.value : ""
    const to = this.hasDateToTarget ? this.dateToTarget.value : ""
    this._filters.date_from = from
    this._filters.date_to = to
    this._updateFilterBadge()
    this._persistFilters()
    this._resetAndLoad()
  }

  clearFilters() {
    // Reset type checkboxes
    this.typeCheckboxTargets.forEach(cb => cb.checked = true)
    if (this.hasTypeToggleAllTarget) {
      this.typeToggleAllTarget.checked = true
      this.typeToggleAllTarget.indeterminate = false
    }
    this._updateTypeBadge()

    // Reset date presets to "All Dates"
    if (this.hasDatePresetsTarget) {
      this.datePresetsTarget.querySelectorAll("button").forEach((btn, i) => {
        if (i === 0) {
          btn.classList.add("bg-brand/10", "text-brand", "font-medium", "border-brand/30")
          btn.classList.remove("text-gray-500", "hover:bg-gray-50", "border-gray-200")
        } else {
          btn.classList.remove("bg-brand/10", "text-brand", "font-medium", "border-brand/30")
          btn.classList.add("text-gray-500", "hover:bg-gray-50", "border-gray-200")
        }
      })
    }

    // Reset date inputs
    if (this.hasDateFromTarget) this.dateFromTarget.value = ""
    if (this.hasDateToTarget) this.dateToTarget.value = ""
    if (this.hasDateLabelTarget) this.dateLabelTarget.textContent = this.datePresetsTarget?.querySelector("button")?.dataset?.label || "All Dates"

    // Clear filter state
    delete this._filters.type_ids
    this._filters.date_from = ""
    this._filters.date_to = ""

    this._updateFilterBadge()
    this._persistFilters()
    this._resetAndLoad()
  }

  selectDatePreset(event) {
    const preset = event.currentTarget.dataset.preset
    const label = event.currentTarget.dataset.label

    if (this.hasDateLabelTarget) {
      this.dateLabelTarget.textContent = label
    }

    // Update chip styles
    if (this.hasDatePresetsTarget) {
      this.datePresetsTarget.querySelectorAll("button").forEach(btn => {
        if (btn === event.currentTarget) {
          btn.classList.add("bg-brand/10", "text-brand", "font-medium", "border-brand/30")
          btn.classList.remove("text-gray-500", "hover:bg-gray-50", "border-gray-200")
        } else {
          btn.classList.remove("bg-brand/10", "text-brand", "font-medium", "border-brand/30")
          btn.classList.add("text-gray-500", "hover:bg-gray-50", "border-gray-200")
        }
      })
    }

    if (!preset) {
      this._filters.date_from = ""
      this._filters.date_to = ""
    } else {
      const today = new Date()
      const from = new Date(today)
      from.setDate(from.getDate() - parseInt(preset))
      this._filters.date_from = this._formatDate(from)
      this._filters.date_to = this._formatDate(today)
    }

    this._updateFilterBadge()
    this._persistFilters()
    this._resetAndLoad()
  }

  // ── View switcher ──────────────────────────────────────────────────

  switchView(event) {
    const view = event.currentTarget.dataset.view
    if (view === this.currentViewValue) return

    this.currentViewValue = view

    // Update button styles
    this.viewSwitcherTarget.querySelectorAll("button").forEach(btn => {
      if (btn.dataset.view === view) {
        btn.classList.add("text-brand", "font-bold")
        btn.classList.remove("text-text-muted", "font-medium", "hover:text-gray-700")
      } else {
        btn.classList.remove("text-brand", "font-bold")
        btn.classList.add("text-text-muted", "font-medium", "hover:text-gray-700")
      }
    })

    // Toggle table / calendar visibility
    if (view === "calendar") {
      if (this.hasTableContainerTarget) this.tableContainerTarget.classList.add("hidden")
      if (this.hasSentinelTarget) this.sentinelTarget.classList.add("hidden")
      if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden")
      if (this.hasCalendarContainerTarget) this.calendarContainerTarget.classList.remove("hidden")
      this._loadCalendar()
    } else {
      if (this.hasCalendarContainerTarget) this.calendarContainerTarget.classList.add("hidden")
      this.closePopover()
      if (this.hasTableContainerTarget) this.tableContainerTarget.classList.remove("hidden")
      // Load table data if it hasn't been loaded yet
      if (this._reportIds.length === 0 && !this._loading) {
        this._resetAndLoad()
      }
    }

    // Persist preference
    this._saveViewMode(view)
  }

  // ── Calendar ─────────────────────────────────────────────────────

  calendarPrevMonth() {
    this._calMonth--
    if (this._calMonth < 1) { this._calMonth = 12; this._calYear-- }
    this._loadCalendar()
  }

  calendarNextMonth() {
    this._calMonth++
    if (this._calMonth > 12) { this._calMonth = 1; this._calYear++ }
    this._loadCalendar()
  }

  calendarToday() {
    const now = new Date()
    this._calMonth = now.getMonth() + 1
    this._calYear = now.getFullYear()
    this._loadCalendar()
  }

  calendarDayClick(event) {
    const cell = event.currentTarget
    const dateStr = cell.dataset.date
    if (!dateStr || !this._calData?.days?.[dateStr]) return

    const dayData = this._calData.days[dateStr]
    if (!dayData.reports.length) return

    this._showPopover(cell, dateStr, dayData.reports)
  }

  closePopover() {
    if (this.hasCalendarPopoverTarget) {
      this.calendarPopoverTarget.classList.add("hidden")
    }
    document.removeEventListener("click", this._onClickOutsidePopover)

    // Remove active cell highlight
    if (this.hasCalendarGridTarget) {
      this.calendarGridTarget.querySelectorAll("[data-active-cell]").forEach(el => {
        el.removeAttribute("data-active-cell")
        el.classList.remove("!bg-gray-100")
      })
    }
  }

  popoverOpenReport(event) {
    event.preventDefault()
    const reportId = event.currentTarget.dataset.reportId
    this.closePopover()
    if (reportId) this._openDetail(reportId)
  }

  // ── Detail modal ──────────────────────────────────────────────────

  openReport(event) {
    const reportId = event.currentTarget.dataset.reportId
    if (!reportId) return
    const report = this._reportsMap[String(reportId)]
    if (report && report.generating && !report.generation_failed) return
    if (report && report.kind === "RSO" && report.pdf_url) {
      window.open(report.pdf_url, "_blank")
      return
    }
    this._openDetail(reportId)
  }

  closeDetail() {
    this._closeModal()
  }

  // Backdrop: track mousedown target so drags from inside don't close
  backdropMousedown(event) {
    this._mousedownTarget = event.target
  }

  closeOnBackdrop(event) {
    if (event.target === event.currentTarget && this._mousedownTarget === event.currentTarget) {
      this._closeModal()
    }
    this._mousedownTarget = null
  }

  prevReport() {
    const idx = this._reportIds.indexOf(this._activeReportId)
    if (idx > 0) this._openDetail(this._reportIds[idx - 1])
  }

  nextReport() {
    const idx = this._reportIds.indexOf(this._activeReportId)
    if (idx >= 0 && idx < this._reportIds.length - 1) {
      this._openDetail(this._reportIds[idx + 1])
    }
  }

  // ── Internal ───────────────────────────────────────────────────────

  _initCalendarView() {
    // Set button styles for calendar active
    if (this.hasViewSwitcherTarget) {
      this.viewSwitcherTarget.querySelectorAll("button").forEach(btn => {
        if (btn.dataset.view === "calendar") {
          btn.classList.add("text-brand", "font-bold")
          btn.classList.remove("text-text-muted", "font-medium", "hover:text-gray-700")
        } else {
          btn.classList.remove("text-brand", "font-bold")
          btn.classList.add("text-text-muted", "font-medium", "hover:text-gray-700")
        }
      })
    }

    // Show calendar, hide table
    if (this.hasTableContainerTarget) this.tableContainerTarget.classList.add("hidden")
    if (this.hasSentinelTarget) this.sentinelTarget.classList.add("hidden")
    if (this.hasCalendarContainerTarget) this.calendarContainerTarget.classList.remove("hidden")
    this._loadCalendar()
  }

  _saveViewMode(mode) {
    if (!this.saveViewModeUrlValue) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.saveViewModeUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded", "X-CSRF-Token": token },
      credentials: "same-origin",
      body: `mode=${encodeURIComponent(mode)}`
    })
  }

  _persistFilters() {
    if (!this.saveFiltersUrlValue) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    const filters = {}

    // Save unchecked type IDs (so we know which to uncheck on restore)
    const totalTypes = this.typeCheckboxTargets.length
    const checkedTypes = this.typeCheckboxTargets.filter(cb => cb.checked)
    if (checkedTypes.length < totalTypes && checkedTypes.length > 0) {
      filters.type_ids = checkedTypes.map(cb => cb.value)
    }

    // Save active date preset
    if (this.hasDatePresetsTarget) {
      const activePreset = [...this.datePresetsTarget.querySelectorAll("button")].find(b => b.classList.contains("bg-brand/10"))
      if (activePreset && activePreset.dataset.preset) filters.date_preset = activePreset.dataset.preset
    }

    // Save custom date range
    if (this._filters.date_from) filters.date_from = this._filters.date_from
    if (this._filters.date_to) filters.date_to = this._filters.date_to

    fetch(this.saveFiltersUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": token
      },
      credentials: "same-origin",
      body: JSON.stringify({ filters })
    })
  }

  _restoreSavedFilters() {
    const saved = this.savedFiltersValue || {}

    // Restore type checkboxes
    if (saved.type_ids && this.typeCheckboxTargets.length > 0) {
      const savedIds = saved.type_ids.map(String)
      this.typeCheckboxTargets.forEach(cb => {
        cb.checked = savedIds.includes(cb.value)
      })
      // Update toggle-all state
      const checkboxes = this.typeCheckboxTargets
      const checked = checkboxes.filter(cb => cb.checked)
      if (this.hasTypeToggleAllTarget) {
        if (checked.length === checkboxes.length) {
          this.typeToggleAllTarget.checked = true
          this.typeToggleAllTarget.indeterminate = false
        } else if (checked.length === 0) {
          this.typeToggleAllTarget.checked = false
          this.typeToggleAllTarget.indeterminate = false
        } else {
          this.typeToggleAllTarget.checked = false
          this.typeToggleAllTarget.indeterminate = true
        }
      }
      this._filters.type_ids = savedIds
      this._updateTypeBadge()
    }

    // Restore date preset
    if (saved.date_preset && this.hasDatePresetsTarget) {
      const presetBtn = [...this.datePresetsTarget.querySelectorAll("button")].find(b => b.dataset.preset === saved.date_preset)
      if (presetBtn) {
        // Update chip styles
        this.datePresetsTarget.querySelectorAll("button").forEach(btn => {
          if (btn === presetBtn) {
            btn.classList.add("bg-brand/10", "text-brand", "font-medium", "border-brand/30")
            btn.classList.remove("text-gray-500", "hover:bg-gray-50", "border-gray-200")
          } else {
            btn.classList.remove("bg-brand/10", "text-brand", "font-medium", "border-brand/30")
            btn.classList.add("text-gray-500", "hover:bg-gray-50", "border-gray-200")
          }
        })
        // Apply the date filter
        const preset = saved.date_preset
        const today = new Date()
        const from = new Date(today)
        from.setDate(from.getDate() - parseInt(preset))
        this._filters.date_from = this._formatDate(from)
        this._filters.date_to = this._formatDate(today)
      }
    } else if (saved.date_from || saved.date_to) {
      // Restore custom date range
      if (saved.date_from) {
        this._filters.date_from = saved.date_from
        if (this.hasDateFromTarget) this.dateFromTarget.value = saved.date_from
      }
      if (saved.date_to) {
        this._filters.date_to = saved.date_to
        if (this.hasDateToTarget) this.dateToTarget.value = saved.date_to
      }
      // Deselect "All Dates" preset
      if (this.hasDatePresetsTarget) {
        this.datePresetsTarget.querySelectorAll("button").forEach(btn => {
          btn.classList.remove("bg-brand/10", "text-brand", "font-medium", "border-brand/30")
          btn.classList.add("text-gray-500", "hover:bg-gray-50", "border-gray-200")
        })
      }
    }

    this._updateFilterBadge()
  }

  _updateTypeBadge() {
    if (!this.hasTypeBadgeTarget) return
    const total = this.typeCheckboxTargets.length
    const checked = this.typeCheckboxTargets.filter(cb => cb.checked).length
    if (checked === total || checked === 0) {
      this.typeBadgeTarget.classList.add("hidden")
      this.typeBadgeTarget.textContent = ""
    } else {
      this.typeBadgeTarget.textContent = `${checked}/${total}`
      this.typeBadgeTarget.classList.remove("hidden")
    }
  }

  _updateBreadcrumbCount(count) {
    const el = document.getElementById("reports-breadcrumb-count")
    if (el) el.textContent = count
  }

  _decrementBreadcrumbCount() {
    const el = document.getElementById("reports-breadcrumb-count")
    if (el) {
      const current = parseInt(el.textContent) || 0
      if (current > 0) el.textContent = current - 1
    }
  }

  _updateFilterBadge() {
    let count = 0

    // Count type filter
    const totalTypes = this.typeCheckboxTargets.length
    const checkedTypes = this.typeCheckboxTargets.filter(cb => cb.checked).length
    if (totalTypes > 0 && checkedTypes < totalTypes && checkedTypes > 0) count++

    // Count date filter
    if ((this._filters.date_from && this._filters.date_from !== "") ||
        (this._filters.date_to && this._filters.date_to !== "")) count++

    if (this.hasFilterBadgeTarget) {
      if (count > 0) {
        this.filterBadgeTarget.textContent = count
        this.filterBadgeTarget.classList.remove("hidden")
        this.filterBadgeTarget.classList.add("inline-flex")
      } else {
        this.filterBadgeTarget.classList.add("hidden")
        this.filterBadgeTarget.classList.remove("inline-flex")
        this.filterBadgeTarget.textContent = ""
      }
    }

    // Show/hide clear filters row
    if (this.hasClearFiltersRowTarget) {
      this.clearFiltersRowTarget.style.display = count > 0 ? "flex" : "none"
    }
  }

  _applyTypeFilter() {
    const checkboxes = this.typeCheckboxTargets
    const checked = checkboxes.filter(cb => cb.checked)

    if (checked.length === checkboxes.length || checked.length === 0) {
      delete this._filters.type_ids
    } else {
      this._filters.type_ids = checked.map(cb => cb.value)
    }
    this._resetAndLoad()
  }

  _resetAndLoad() {
    // Cancel any in-flight page load first: its late response would repaint
    // the just-cleared table with the OLD filters' rows, and _loadPage would
    // bail on this._loading — the "filter isn't filtering" bug (Larry
    // 2026-08-25).
    this._fetchAbort?.abort()
    this._loading = false
    this._page = 1
    this._isLastPage = false
    this._rowIndex = 0
    this._reportIds = []
    this._reportsMap = {}
    this.tableBodyTarget.innerHTML = ""

    if (this.currentViewValue === "calendar") {
      this._loadCalendar()
    } else {
      this._loadPage(1)
    }
  }

  async _loadPage(page) {
    if (this._loading || this._isLastPage) return
    this._loading = true
    const abort = (this._fetchAbort = new AbortController())

    try {
      const url = new URL(this.reportsUrlValue, window.location.origin)
      url.searchParams.set("page", page)

      if (this._filters.search) url.searchParams.set("search", this._filters.search)
      if (this._filters.type_ids) {
        this._filters.type_ids.forEach(id => url.searchParams.append("type_ids[]", id))
      }
      if (this._filters.author_id) url.searchParams.set("author_id", this._filters.author_id)
      if (this._filters.date_from) url.searchParams.set("date_from", this._filters.date_from)
      if (this._filters.date_to) url.searchParams.set("date_to", this._filters.date_to)

      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": token
        },
        credentials: "same-origin",
        signal: abort.signal
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const data = await response.json()

      // Hide skeleton
      if (this.hasSkeletonTarget) {
        this.skeletonTarget.classList.add("hidden")
      }

      // Render rows and collect IDs
      data.reports.forEach(report => {
        this._reportIds.push(String(report.id))
        this._reportsMap[String(report.id)] = report
        if (!this._sort.key) {
          this.tableBodyTarget.insertAdjacentHTML("beforeend", this._renderRow(report))
        }
        // Auto-poll generating customer reports (e.g. after page reload)
        if (report.generating && report.poll_url) {
          this._pollGeneratingReport(String(report.id), report.poll_url)
        }
      })
      if (this._sort.key) this._reRenderTable()

      // Update count badge (lives outside controller scope, in breadcrumb)
      this._updateBreadcrumbCount(data.total_count)

      // Handle empty state
      if (page === 1 && data.reports.length === 0) {
        this.emptyTarget.classList.remove("hidden")
        if (this.hasTableContainerTarget) this.tableContainerTarget.classList.add("hidden")
        if (this.hasSentinelTarget) this.sentinelTarget.classList.add("hidden")
      } else {
        this.emptyTarget.classList.add("hidden")
        if (this.hasTableContainerTarget) this.tableContainerTarget.classList.remove("hidden")
      }

      this._isLastPage = data.is_last_page
      this._page = page + 1

      // Deep link: auto-open modal after first page loads
      if (page === 1 && this._pendingDeepLinkId) {
        const id = this._pendingDeepLinkId
        const shouldDistribute = this._pendingDistribute
        this._pendingDeepLinkId = null
        this._pendingDistribute = false
        // Delay slightly so the DOM is ready
        requestAnimationFrame(async () => {
          await this._openDetail(id)
          if (shouldDistribute) this.showDistribute()
        })
      }

      // Set up infinite scroll sentinel
      if (!this._isLastPage && this.hasSentinelTarget) {
        this.sentinelTarget.classList.remove("hidden")
        this._observer.observe(this.sentinelTarget)
      } else if (this.hasSentinelTarget) {
        this.sentinelTarget.classList.add("hidden")
        this._observer.disconnect()
      }
    } catch (error) {
      if (error.name === "AbortError") return
      console.error("[Reports] Failed to load page:", error)
      if (this.hasSkeletonTarget) {
        this.skeletonTarget.classList.add("hidden")
      }
    } finally {
      // A superseded call must not clear the flag the replacement just set.
      if (this._fetchAbort === abort) {
        this._fetchAbort = null
        this._loading = false
      }
    }
  }

  _renderRow(report) {
    const typeBadgeColor = this._typeColor(report.type_name)
    const typeLabel = report.type_label || report.type_name || ""
    const isDraft = report.is_draft
    const stripe = this._rowIndex % 2 === 1 ? "bg-gray-50/60" : "bg-white"
    this._rowIndex++
    const cell = "px-2 xl:px-3 py-2 xl:py-2.5 whitespace-nowrap"
    const draftBadge = isDraft
      ? `<span class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-blue-100 text-blue-600 mr-1.5 translate-y-[-1px]">Draft</span>`
      : ""
    const locationIcon = report.has_geolocation
      ? `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-[18px] h-[18px] shrink-0 mr-1 translate-y-[1px]" title="At Jobsite"><path fill="rgba(115,193,29,0.85)" d="M12 10.577c.385 0 .742-.09 1.072-.27.33-.179.591-.42.784-.72.192-.302.288-.632.288-.99a1.85 1.85 0 0 0-.288-1 2.076 2.076 0 0 0-.784-.732c-.33-.18-.687-.269-1.072-.269a2.15 2.15 0 0 0-1.835 1 1.85 1.85 0 0 0-.289 1c0 .551.206 1.02.619 1.404.412.385.914.577 1.505.577zM12 3c1.1 0 2.11.25 3.03.75a5.865 5.865 0 0 1 2.166 2.02c.536.858.804 1.8.804 2.826 0 .782-.206 1.68-.619 2.692-.357.86-.852 1.789-1.484 2.789a36.74 36.74 0 0 1-1.794 2.48 30.35 30.35 0 0 1-1.484 1.77L12 19l-.619-.673a30.35 30.35 0 0 1-1.484-1.77 36.74 36.74 0 0 1-1.794-2.48c-.632-1-1.127-1.93-1.484-2.789C6.206 10.276 6 9.378 6 8.596c0-1.025.268-1.968.804-2.827A5.865 5.865 0 0 1 8.97 3.75 6.25 6.25 0 0 1 12 3z"/></svg>`
      : ""
    const procoreIcon = report.from_procore
      ? `<img src="/procore-icon.png" class="h-[18px] opacity-50 shrink-0 mr-1 -my-0.5" style="width:auto" title="From Procore">`
      : ""
    const specialIcon = report.is_special
      ? `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" fill="currentColor" class="w-4 h-4 shrink-0 mr-1 text-gray-400" title="Only visible to owners/stakeholders"><path d="M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.6 66.4-86.1 79.9-118.4c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C465.5 68.8 400.8 32 320 32c-68.2 0-125 26.3-169.3 60.8L38.8 5.1zm151 118.3C226 97.7 269.5 80 320 80c65.2 0 118.8 29.6 159.9 67.7C518.4 183.5 545 226 558.6 256c-12.6 28-36.6 66.8-70.9 100.9l-53.8-42.2c9.1-17.6 14.2-37.5 14.2-58.7c0-70.7-57.3-128-128-128c-32.2 0-61.7 11.9-84.2 31.5l-46.1-36.1zM394.9 284.2l-81.5-63.9c4.2-8.5 6.6-18.2 6.6-28.3c0-5.5-.7-10.9-2-16c.7 0 1.3 0 2 0c44.2 0 80 35.8 80 80c0 9.9-1.8 19.4-5.1 28.2zm9.4 130.3C378.8 425.4 350.7 432 320 432c-65.2 0-118.8-29.6-159.9-67.7C121.6 328.5 95 286 81.4 256c8.3-18.4 21.5-41.5 39.4-64.8L83.1 161.5C60.3 191.2 44 220.8 34.5 243.7c-3.3 7.9-3.3 16.7 0 24.6c14.9 35.7 46.2 87.7 93 131.1C174.5 443.2 239.2 480 320 480c47.8 0 89.9-12.9 126.2-32.5l-41.9-33zM192 256c0 70.7 57.3 128 128 128c13.3 0 26.1-2 38.2-5.8L302 334c-23.5-5.4-43.1-21.2-53.7-42.3l-56.1-44.2c-.2 2.8-.3 5.6-.3 8.5z"/></svg>`
      : ""
    const isGenerating = report.generating && !report.generation_failed
    const rowCursor = isGenerating ? "cursor-default opacity-75" : "cursor-pointer hover:bg-brand/[0.04]"

    let notesContent
    if (report.generation_failed) {
      notesContent = `<span class="text-red-500 truncate">Report generation failed</span>`
    } else if (isGenerating) {
      notesContent = `<div class="w-3.5 h-3.5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin shrink-0 mr-2"></div><span class="text-gray-700 truncate">${this._escapeHtml(this.generatingRowTextValue)}</span>`
    } else {
      const emptyFallback = ["DDS", "APR"].includes(report.kind) ? "Clique para ver todos os detalhes deste relatório." : ""
      const emptyClass = !report.narration && ["DDS", "APR"].includes(report.kind) ? " italic text-gray-400" : ""
      notesContent = `<span class="truncate${emptyClass}">${this._escapeHtml(report.narration || emptyFallback)}</span>`
    }
    // Files column (Larry's sheet, 2026-09-06): paperclip + count, empty when none.
    const files = Number(report.files_count) || 0
    const clip = files > 0
      ? `<span class="inline-flex items-center gap-0.5 text-gray-500" title="${files} file${files === 1 ? "" : "s"} attached">${this.paperclipHtmlValue || ""}<span class="text-[13px]">${files}</span></span>`
      : ""

    // Each cell by its column key; the row shows the page's columns in order.
    const cells = {
      date: `<td class="${cell} text-gray-700">${this._escapeHtml(this._displayDate(report.formatted_date, report.date))}</td>`,
      type: `<td class="${cell}">
        <span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-semibold text-gray-600 whitespace-nowrap bg-white border border-gray-300">
          ${this._escapeHtml(typeLabel)}
        </span>
      </td>`,
      notes: `<td class="${cell} text-gray-500 text-[14px] max-w-sm xl:max-w-lg"><div class="flex items-center truncate">${locationIcon}${procoreIcon}${specialIcon}${draftBadge}${notesContent}</div></td>`,
      author: `<td class="px-4 xl:px-5 py-2 xl:py-2.5 whitespace-nowrap text-gray-700">${this._escapeHtml(report.author_name || "")}</td>`,
      photos: `<td class="px-4 xl:px-5 py-[2px] whitespace-nowrap">${this._photoTiles(report)}</td>`,
      files: `<td class="px-2 py-2 xl:py-2.5 whitespace-nowrap text-center">${clip}</td>`,
      people: `<td class="px-4 xl:px-5 py-2 xl:py-2.5 whitespace-nowrap text-center text-gray-500">${this._peopleBadge(report.people_count)}</td>`,
      number: `<td class="${cell} text-gray-700">${this._escapeHtml(report.report_number != null ? String(report.report_number) : "")}</td>`,
      weather: `<td class="${cell} text-gray-700">${this._escapeHtml(report.weather || "")}</td>`,
      status: `<td class="${cell}">${isDraft ? `<span class="inline-flex items-center px-1.5 py-0.5 rounded text-[13px] font-semibold bg-blue-100 text-blue-700">Draft</span>` : `<span class="inline-flex items-center px-1.5 py-0.5 rounded text-[13px] font-semibold bg-green-100 text-green-700">Published</span>`}</td>`,
      updated: `<td class="${cell} text-gray-700">${this._escapeHtml(report.updated_formatted || "")}</td>`,
      jobsite: `<td class="${cell} text-center">${report.has_geolocation ? `<span class="text-green-600" title="At jobsite">✓</span>` : `<span class="text-gray-300">—</span>`}</td>`
    }
    const order = this.hasColumnsValue && this.columnsValue.length ? this.columnsValue : ["date", "type", "notes", "author", "photos", "files", "people"]

    return `<tr class="${stripe} border-b border-gray-100 transition-colors ${rowCursor}"
                data-action="click->reports#openReport"
                data-report-id="${report.id}">
      ${order.map((k) => cells[k] || "").join("\n      ")}
    </tr>`
  }

  _photoTiles(report) {
    const urls = report.thumbnail_urls || []
    const total = report.photos_count || 0
    if (total === 0) return ""

    const maxTiles = 5
    const hasExtra = total > maxTiles
    const showCount = Math.min(urls.length, hasExtra ? maxTiles : total)
    const extra = total - showCount

    let html = `<div class="flex gap-0.5">`
    for (let i = 0; i < showCount; i++) {
      html += `<div class="h-[34px] w-[34px] rounded bg-gray-100 overflow-hidden shrink-0 animate-pulse">
        <img src="${this._escapeHtml(urls[i])}"
             class="w-full h-full object-cover opacity-0 transition-opacity duration-300"
             data-controller="img-handler"
             data-action="load->img-handler#showLoadedAndStopPulse error->img-handler#greyOutAndRemove" />
      </div>`
    }
    if (extra > 0) {
      html += `<div class="h-[34px] w-[34px] rounded bg-gray-200 shrink-0 flex items-center justify-center">
        <span class="text-[10px] font-medium text-gray-500">+${extra}</span>
      </div>`
    }
    html += `</div>`
    return html
  }

  _peopleBadge(count) {
    if (!count) return ""
    const icon = `<svg class="w-3.5 h-3.5" viewBox="0 0 512 512" fill="none" stroke="currentColor"><path d="M402 168c-2.93 40.67-33.1 72-66 72s-63.12-31.32-66-72c-3-42.31 26.37-72 66-72s69 30.46 66 72z" stroke-linecap="round" stroke-linejoin="round" stroke-width="32"/><path d="M336 304c-65.17 0-127.84 32.37-143.54 95.41-2.08 8.34 3.15 16.59 11.72 16.59h263.65c8.57 0 13.77-8.25 11.72-16.59C463.85 335.36 401.18 304 336 304z" stroke-miterlimit="10" stroke-width="32"/><path d="M200 185.94c-2.34 32.48-26.72 58.06-53 58.06s-50.7-25.57-53-58.06C91.61 152.15 115.34 128 147 128s55.39 24.77 53 57.94z" stroke-linecap="round" stroke-linejoin="round" stroke-width="32"/><path d="M206 306c-18.05-8.27-37.93-11.45-59-11.45-52 0-102.1 25.85-114.65 76.2-1.65 6.66 2.53 13.25 9.37 13.25H154" stroke-linecap="round" stroke-miterlimit="10" stroke-width="32"/></svg>`
    return `<span class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
      ${icon}<span class="text-[13px] font-medium">${count}</span>
    </span>`
  }

  _typeColor(typeName) {
    const hexColors = [
      "#3B82F6", "#9333EA", "#14B8A6", "#6366F1", "#EC4899", "#06B6D4", "#F97316", "#10B981",
      "#F43F5E", "#8B5CF6", "#F59E0B", "#84CC16", "#0EA5E9", "#D946EF", "#78716C", "#EF4444"
    ]
    let hex = "#6B7280"
    if (typeName) {
      // FNV-1a hash
      let hash = 0x811c9dc5
      for (let i = 0; i < typeName.length; i++) {
        hash ^= typeName.charCodeAt(i)
        hash = Math.imul(hash, 0x01000193)
      }
      // Fibonacci hashing: golden-ratio multiply spreads bits, upper 4 select index
      const index = (Math.imul(hash >>> 0, 2654435769) >>> 0) >>> 28
      hex = hexColors[index]
    }
    return `background-color:${hex}12;color:${hex};border:1px solid ${hex}4D;`
  }

  _displayDate(formatted, raw) {
    if (!formatted || !raw) return formatted || ""
    const currentYear = new Date().getFullYear().toString()
    if (raw.startsWith(currentYear)) {
      return formatted.replace(` ${currentYear}`, "")
    }
    return formatted
  }

  _formatDate(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }

  _escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  // ── Detail modal internals ──────────────────────────────────────

  async _openDetail(reportId) {
    reportId = String(reportId)
    this._activeReportId = reportId
    this._modalOpen = true

    // Show panel with loading state — clear old content and inject skeleton
    document.body.style.overflow = "hidden"
    this.detailModalTarget.classList.remove("hidden")
    this.detailContentTarget.innerHTML = `
      <div class="p-6 animate-pulse space-y-4">
        <div class="flex items-center gap-3">
          <div class="w-11 h-11 rounded-full bg-gray-200"></div>
          <div class="space-y-2 flex-1">
            <div class="h-4 bg-gray-200 rounded w-36"></div>
            <div class="h-3 bg-gray-200 rounded w-24"></div>
          </div>
        </div>
        <div class="flex gap-2">
          <div class="h-7 bg-gray-100 rounded-full w-40"></div>
          <div class="h-7 bg-gray-100 rounded-full w-28"></div>
        </div>
        <div class="h-24 bg-gray-100 rounded-lg"></div>
        <div class="h-4 bg-gray-200 rounded w-20"></div>
        <div class="h-16 bg-gray-100 rounded"></div>
      </div>`
    // Reset header to placeholder while loading
    if (this.hasDetailTitleTarget) {
      this.detailTitleTarget.innerHTML = `<span class="text-base font-semibold text-gray-800">Report</span>`
    }
    if (this.hasEditLinkTarget) this.editLinkTarget.classList.add("hidden")
    if (this.hasPublishBtnTarget) this.publishBtnTarget.classList.add("hidden")
    if (this.hasReleaseEditBtnTarget) this.releaseEditBtnTarget.classList.add("hidden")
    if (this.hasDistributeBtnTarget) this.distributeBtnTarget.classList.add("hidden")
    document.addEventListener("keydown", this._onKeydown)

    // Animate slide-in
    requestAnimationFrame(() => {
      if (this.hasDetailBackdropTarget) this.detailBackdropTarget.classList.replace("opacity-0", "opacity-100")
      if (this.hasDetailPanelTarget) this.detailPanelTarget.classList.replace("translate-x-full", "translate-x-0")
    })

    // Highlight active row
    this._highlightRow(reportId)

    // Update prev/next buttons
    this._updateNavButtons()

    // Push URL
    const showUrl = `/projects/${this.projectIdValue}/daily_reports/${reportId}`
    window.history.pushState({ reportId }, "", showUrl)

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(showUrl, {
        headers: { "Accept": "text/html", "X-CSRF-Token": token },
        credentials: "same-origin"
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const html = await response.text()

      this.detailContentTarget.innerHTML = html

      // Extract header info from template and place in modal header
      if (this.hasDetailTitleTarget) {
        const tpl = this.detailContentTarget.querySelector("#report-header-info")
        if (tpl) {
          const content = tpl.content.cloneNode(true)

          this.detailTitleTarget.innerHTML = ""
          this.detailTitleTarget.appendChild(content)
          tpl.remove()
        } else {
          this.detailTitleTarget.innerHTML = `<span class="text-base font-semibold text-gray-800">Report</span>`
        }
      }

      // Read permissions from the detail response
      const permsEl = this.detailContentTarget.querySelector("#report-permissions")
      this._activeReportPublished = permsEl?.dataset.published === "true"
      const canPublish = permsEl?.dataset.canPublish === "true"
      const canUnpublish = permsEl?.dataset.canUnpublish === "true"
      const canReleaseEdit = permsEl?.dataset.canReleaseEdit === "true"
      const canEdit = permsEl?.dataset.canEdit === "true"
      const canDelete = permsEl?.dataset.canDelete === "true"
      const canDistribute = permsEl?.dataset.canDistribute === "true"
      if (permsEl) permsEl.remove()

      // Show action buttons based on permissions
      if (this.hasExportPdfLinkTarget) {
        const reportData = this._reportsMap[reportId]
        if (reportData?.pdf_url) {
          this.exportPdfLinkTarget.href = reportData.pdf_url
        } else {
          this.exportPdfLinkTarget.href = `/projects/${this.projectIdValue}/daily_reports/${reportId}/export_pdf`
        }
      }
      if (this.hasEditLinkTarget) {
        if (canEdit) {
          this.editLinkTarget.href = `/projects/${this.projectIdValue}/daily_reports/${reportId}/edit`
          this.editLinkTarget.classList.remove("hidden")
          this.editLinkTarget.classList.add("inline-flex")
        } else {
          this.editLinkTarget.classList.add("hidden")
          this.editLinkTarget.classList.remove("inline-flex")
        }
      }
      if (this.hasDeleteBtnTarget) {
        if (canDelete) {
          this.deleteBtnTarget.classList.remove("hidden")
          this.deleteBtnTarget.classList.add("inline-flex")
        } else {
          this.deleteBtnTarget.classList.add("hidden")
          this.deleteBtnTarget.classList.remove("inline-flex")
        }
      }
      if (this.hasPublishBtnTarget) {
        if (canPublish || canUnpublish) {
          this.publishBtnTarget.classList.remove("hidden")
          this.publishBtnTarget.classList.add("inline-flex")
          this._updatePublishButton(this._activeReportPublished)
        } else {
          this.publishBtnTarget.classList.add("hidden")
          this.publishBtnTarget.classList.remove("inline-flex")
        }
      }
      if (this.hasReleaseEditBtnTarget) {
        if (canReleaseEdit) {
          this.releaseEditBtnTarget.classList.remove("hidden")
          this.releaseEditBtnTarget.classList.add("inline-flex")
        } else {
          this.releaseEditBtnTarget.classList.add("hidden")
          this.releaseEditBtnTarget.classList.remove("inline-flex")
        }
      }
      if (this.hasDistributeBtnTarget) {
        if (canDistribute) {
          this.distributeBtnTarget.classList.remove("hidden")
          this.distributeBtnTarget.classList.add("inline-flex")
        } else {
          this.distributeBtnTarget.classList.add("hidden")
          this.distributeBtnTarget.classList.remove("inline-flex")
        }
      }
    } catch (error) {
      console.error("[Reports] Failed to load report detail:", error)
      this.detailContentTarget.innerHTML = `
        <div class="p-6 text-center text-gray-500">
          <p>Failed to load report.</p>
        </div>`
    }
  }

  showDeleteConfirm() {
    if (this.hasDeleteConfirmModalTarget) {
      this.deleteConfirmModalTarget.classList.remove("hidden")
    }
  }

  cancelDelete() {
    if (this.hasDeleteConfirmModalTarget) {
      this.deleteConfirmModalTarget.classList.add("hidden")
    }
  }

  async confirmDelete() {
    if (!this._activeReportId) return

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const response = await fetch(`/projects/${this.projectIdValue}/daily_reports/${this._activeReportId}`, {
        method: "DELETE",
        headers: { "Accept": "application/json", "X-CSRF-Token": token }
      })

      if (response.ok) {
        // Hide confirmation modal
        if (this.hasDeleteConfirmModalTarget) {
          this.deleteConfirmModalTarget.classList.add("hidden")
        }

        // Remove the row from the table
        const row = this.tableBodyTarget.querySelector(`tr[data-report-id="${this._activeReportId}"]`)
        if (row) row.remove()

        // Remove from internal list
        this._reportIds = this._reportIds.filter(id => id !== this._activeReportId)

        // Update count
        this._decrementBreadcrumbCount()

        // Close the detail panel
        this._closeModal()
      }
    } catch (error) {
      console.error("[Reports] Failed to delete report:", error)
    }
  }

  // ── Publish / Unpublish ─────────────────────────────────────────

  async togglePublish() {
    if (!this._activeReportId) return
    const isPublished = this._activeReportPublished
    const action = isPublished ? "unpublish" : "publish"
    const url = `/projects/${this.projectIdValue}/daily_reports/${this._activeReportId}/${action}`
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Accept": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      this._activeReportPublished = data.published
      this._updatePublishButton(data.published)

      // Update the draft badge in the detail content and table row
      this._updateDetailDraftBadge(data.published)
      this._updateRowDraftBadge(this._activeReportId, data.published)

      // Toggle edit, delete and distribute buttons for the new state. The
      // server says who may now edit or delete: unpublishing someone else's
      // report doesn't make it yours to edit.
      const canEdit = typeof data.can_edit === "boolean" ? data.can_edit : !data.published
      if (this.hasEditLinkTarget) {
        if (canEdit) {
          this.editLinkTarget.href = `/projects/${this.projectIdValue}/daily_reports/${this._activeReportId}/edit`
          this.editLinkTarget.classList.remove("hidden")
          this.editLinkTarget.classList.add("inline-flex")
        } else {
          this.editLinkTarget.classList.add("hidden")
          this.editLinkTarget.classList.remove("inline-flex")
        }
      }
      if (this.hasDeleteBtnTarget && typeof data.can_delete === "boolean") {
        if (data.can_delete) {
          this.deleteBtnTarget.classList.remove("hidden")
          this.deleteBtnTarget.classList.add("inline-flex")
        } else {
          this.deleteBtnTarget.classList.add("hidden")
          this.deleteBtnTarget.classList.remove("inline-flex")
        }
      }
      if (this.hasDistributeBtnTarget) {
        if (data.published) {
          this.distributeBtnTarget.classList.remove("hidden")
          this.distributeBtnTarget.classList.add("inline-flex")
        } else {
          this.distributeBtnTarget.classList.add("hidden")
          this.distributeBtnTarget.classList.remove("inline-flex")
        }
      }

      const words = this.hasPublishBtnTarget ? this.publishBtnTarget.dataset : {}
      this._showToast(data.published ? (words.publishedToast || "Report published.") : (words.unpublishedToast || "Report unpublished."))
    } catch (error) {
      console.error("[Reports] Failed to toggle publish:", error)
      const words = this.hasPublishBtnTarget ? this.publishBtnTarget.dataset : {}
      this._showToast(words.publishFailed || "Failed to update report status. Please try again.", "error")
    }
  }

  // ── Release Edit (owner/admin unpublishes another user's report) ──

  async releaseEdit() {
    if (!this._activeReportId) return
    const reportId = this._activeReportId
    const url = `/projects/${this.projectIdValue}/daily_reports/${reportId}/unpublish`
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Accept": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      // Remove the row from the table
      const row = this.tableBodyTarget.querySelector(`tr[data-report-id="${reportId}"]`)
      if (row) row.remove()

      // Remove from internal list and update count
      this._reportIds = this._reportIds.filter(id => id !== reportId)
      this._decrementBreadcrumbCount()

      // Close the detail panel and show toast
      this._closeModal()
      this._showToast("Report released for edit.")
    } catch (error) {
      console.error("[Reports] Failed to release edit:", error)
      this._showToast("Failed to release report. Please try again.", "error")
    }
  }

  // ── Distribution ──────────────────────────────────────────────

  async showDistribute() {
    if (!this._activeReportId) return
    if (this.hasDistributeModalTarget) {
      this.distributeModalTarget.classList.remove("hidden")
    }

    // Load members
    const url = `/projects/${this.projectIdValue}/daily_reports/${this._activeReportId}/distribute_members`
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(url, {
        headers: { "Accept": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      this._renderDistributeMembers(data.groups)
    } catch (error) {
      console.error("[Reports] Failed to load distribute members:", error)
      if (this.hasDistributeBodyTarget) {
        this.distributeBodyTarget.innerHTML = `<p class="text-sm text-gray-500 py-4 text-center">Failed to load members.</p>`
      }
    }
  }

  closeDistribute() {
    if (this.hasDistributeModalTarget) {
      this.distributeModalTarget.classList.add("hidden")
    }
  }

  distributeToggleAll(event) {
    const checked = event.currentTarget.checked
    if (this.hasDistributeBodyTarget) {
      this.distributeBodyTarget.querySelectorAll('input[type="checkbox"][data-email]').forEach(cb => {
        if (cb.disabled) return // subscribed members are locked-on
        cb.checked = checked
      })
    }
  }

  async sendDistribution() {
    if (!this._activeReportId || !this.hasDistributeBodyTarget) return

    const checkboxes = this.distributeBodyTarget.querySelectorAll('input[type="checkbox"][data-email]:checked')
    const emails = Array.from(checkboxes).map(cb => cb.dataset.email)

    if (emails.length === 0) return

    const btn = this.hasDistributeSendBtnTarget ? this.distributeSendBtnTarget : null
    if (btn) {
      btn.disabled = true
      btn.textContent = "Sending..."
    }

    const url = `/projects/${this.projectIdValue}/daily_reports/${this._activeReportId}/distribute_report`
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": token
        },
        credentials: "same-origin",
        body: JSON.stringify({ emails })
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      // Close modal and update publish state (distribute auto-publishes)
      this.closeDistribute()
      this._activeReportPublished = true
      this._updatePublishButton(true)
      this._updateDetailDraftBadge(true)

      // Reload detail content to show updated distribution history
      this._openDetail(this._activeReportId)
      this._showToast("Report distributed successfully.")
    } catch (error) {
      console.error("[Reports] Failed to distribute:", error)
      this._showToast("Failed to distribute report. Please try again.", "error")
    } finally {
      if (btn) {
        btn.disabled = false
        btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3.5 h-3.5 inline -mt-0.5 mr-1"><path stroke-linecap="round" stroke-linejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5"/></svg>Send`
      }
    }
  }

  _renderDistributeMembers(groups) {
    if (!this.hasDistributeBodyTarget) return

    if (!groups || groups.length === 0) {
      this.distributeBodyTarget.innerHTML = `<p class="text-sm text-gray-500 py-4 text-center">No members found.</p>`
      return
    }

    let html = ""
    groups.forEach(group => {
      html += `<div class="mb-3">
        <b class="text-[13px]">${this._escapeHtml(group.role)} · ${this._escapeHtml(group.company_name)}</b>`

      group.members.forEach(member => {
        const notifiedBadge = member.already_notified
          ? `<span class="text-[13px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full font-medium ml-1">Sent</span>`
          : ""
        // Members subscribed to this report type's email notifications are
        // always included: checkbox is checked + disabled so it can't be unchecked.
        const subscribedBadge = member.email_subscribed
          ? `<span class="text-[13px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full font-medium ml-1" title="Always receives this report type per their notification settings">Subscribed</span>`
          : ""
        const lockAttrs = member.email_subscribed ? "checked disabled" : ""
        const lockClass = member.email_subscribed ? " opacity-60 cursor-not-allowed" : ""
        const nameLabel = member.name
          ? `${this._escapeHtml(member.name)} - ${this._escapeHtml(member.email)}`
          : this._escapeHtml(member.email)
        html += `<div class="mb-1 pl-5 relative">
            <input type="checkbox" data-email="${this._escapeHtml(member.email)}" class="absolute left-0 top-1${lockClass}" style="accent-color: var(--color-brand)" ${lockAttrs}>
            <span class="text-[13px]">${nameLabel}</span>${notifiedBadge}${subscribedBadge}
          </div>`
      })

      html += `</div>`
    })

    this.distributeBodyTarget.innerHTML = html

    // Reset select all
    if (this.hasDistributeSelectAllTarget) {
      this.distributeSelectAllTarget.checked = false
    }
  }

  _updatePublishButton(isPublished) {
    if (!this.hasPublishBtnTarget) return
    const btn = this.publishBtnTarget
    const label = this.hasPublishLabelTarget ? this.publishLabelTarget : null
    const publishText = btn.dataset.publishText || "Publish"
    const unpublishText = btn.dataset.unpublishText || "Unpublish"

    if (isPublished) {
      btn.classList.remove("text-brand", "bg-brand/5", "border-brand/20", "hover:bg-brand/10")
      btn.classList.add("text-amber-700", "bg-amber-50", "border", "border-amber-200", "hover:bg-amber-100")
      if (label) label.textContent = unpublishText
      btn.title = unpublishText
      if (this.hasPublishIconCheckTarget) this.publishIconCheckTarget.classList.add("hidden")
      if (this.hasPublishIconXTarget) this.publishIconXTarget.classList.remove("hidden")
    } else {
      btn.classList.remove("text-amber-700", "bg-amber-50", "border-amber-200", "hover:bg-amber-100")
      btn.classList.add("text-brand", "bg-brand/5", "border", "border-brand/20", "hover:bg-brand/10")
      if (label) label.textContent = publishText
      btn.title = publishText
      if (this.hasPublishIconCheckTarget) this.publishIconCheckTarget.classList.remove("hidden")
      if (this.hasPublishIconXTarget) this.publishIconXTarget.classList.add("hidden")
    }
  }

  _updateDetailDraftBadge(isPublished) {
    // Update the draft badge in the detail header
    if (!this.hasDetailTitleTarget) return
    const badge = this.detailTitleTarget.querySelector(".bg-blue-100.text-blue-600")
    if (isPublished && badge) {
      badge.remove()
    }
    // Note: we don't add the badge back on unpublish since it's simpler to just reload
  }

  _updateRowDraftBadge(reportId, isPublished) {
    const row = this.tableBodyTarget.querySelector(`tr[data-report-id="${reportId}"]`)
    if (!row) return
    const badge = row.querySelector(".bg-blue-100.text-blue-600")
    if (isPublished && badge) {
      badge.remove()
    } else if (!isPublished && !badge) {
      // Add draft badge back when unpublishing
      const notesCell = row.querySelector("td:nth-child(3) .flex")
      if (notesCell) {
        const span = document.createElement("span")
        span.className = "inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-blue-100 text-blue-600 mr-1.5 translate-y-[-1px]"
        span.textContent = "Draft"
        // Insert after location/procore icons, before the text span
        const textSpan = notesCell.querySelector("span.truncate")
        if (textSpan) {
          notesCell.insertBefore(span, textSpan)
        } else {
          notesCell.appendChild(span)
        }
      }
    }
  }

  _closeModal() {
    this._modalOpen = false
    this._activeReportId = null
    document.body.style.overflow = ""
    document.removeEventListener("keydown", this._onKeydown)

    // Animate slide-out
    if (this.hasDetailBackdropTarget) this.detailBackdropTarget.classList.replace("opacity-100", "opacity-0")
    if (this.hasDetailPanelTarget) this.detailPanelTarget.classList.replace("translate-x-0", "translate-x-full")

    // Hide container after animation completes
    const panel = this.hasDetailPanelTarget ? this.detailPanelTarget : null
    const hideAfterTransition = () => {
      this.detailModalTarget.classList.add("hidden")
      if (panel) panel.removeEventListener("transitionend", hideAfterTransition)
    }
    if (panel) {
      panel.addEventListener("transitionend", hideAfterTransition, { once: true })
    } else {
      this.detailModalTarget.classList.add("hidden")
    }

    // Remove row highlight
    this.tableBodyTarget.querySelectorAll("tr[data-active]").forEach(tr => {
      tr.removeAttribute("data-active")
      tr.classList.remove("bg-brand/5")
    })

    // Restore URL to reports list
    const listUrl = `/projects/${this.projectIdValue}/reports`
    window.history.pushState({}, "", listUrl)
  }

  _highlightRow(reportId) {
    // Remove previous highlight
    this.tableBodyTarget.querySelectorAll("tr[data-active]").forEach(tr => {
      tr.removeAttribute("data-active")
      tr.classList.remove("bg-brand/5")
    })

    // Add highlight to active row
    const row = this.tableBodyTarget.querySelector(`tr[data-report-id="${reportId}"]`)
    if (row) {
      row.setAttribute("data-active", "true")
      row.classList.add("bg-brand/5")
      row.scrollIntoView({ block: "nearest" })
    }
  }

  _updateNavButtons() {
    const idx = this._reportIds.indexOf(this._activeReportId)
    if (this.hasPrevBtnTarget) this.prevBtnTarget.disabled = (idx <= 0)
    if (this.hasNextBtnTarget) this.nextBtnTarget.disabled = (idx < 0 || idx >= this._reportIds.length - 1)
  }

  _onKeydown(event) {
    if (!this._modalOpen) return

    if (event.key === "Escape") {
      this._closeModal()
    } else if (event.key === "ArrowLeft") {
      event.preventDefault()
      this.prevReport()
    } else if (event.key === "ArrowRight") {
      event.preventDefault()
      this.nextReport()
    }
  }

  _checkDeepLink() {
    // Check URL path: /projects/:id/daily_reports/:report_id
    const match = window.location.pathname.match(/\/projects\/\d+\/daily_reports\/(\d+)/)
    if (match) {
      this._pendingDeepLinkId = match[1]
      return
    }
    // Check query param: ?report_id=123 (from redirect)
    const params = new URLSearchParams(window.location.search)
    const reportId = params.get("report_id")
    if (reportId) {
      this._pendingDeepLinkId = reportId
      // Check if we should auto-open the distribute modal after detail loads
      if (params.get("distribute") === "true") {
        this._pendingDistribute = true
      }
      // Clean up the query params from URL
      const url = new URL(window.location)
      url.searchParams.delete("report_id")
      url.searchParams.delete("distribute")
      window.history.replaceState({}, "", url.pathname + url.search)
    }
  }

  // ── Customer report events ──────────────────────────────────────

  _onCustomerReportCreated(event) {
    const report = event.detail?.report
    if (!report) return

    const id = String(report.id)

    // Add to internal data
    this._reportIds.unshift(id)
    this._reportsMap[id] = report

    // Insert row at top of table
    if (this.hasTableBodyTarget) {
      this._rowIndex = 0
      this.tableBodyTarget.insertAdjacentHTML("afterbegin", this._renderRow(report))
      // Re-stripe all rows
      this.tableBodyTarget.querySelectorAll("tr").forEach((tr, i) => {
        tr.classList.remove("bg-gray-50/60", "bg-white")
        tr.classList.add(i % 2 === 1 ? "bg-gray-50/60" : "bg-white")
      })
    }

    // Make sure table view is visible
    if (this.hasTableContainerTarget) this.tableContainerTarget.classList.remove("hidden")
    if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden")

    // Increment breadcrumb count
    const el = document.getElementById("reports-breadcrumb-count")
    if (el) el.textContent = (parseInt(el.textContent) || 0) + 1
  }

  _onCustomerReportReady(event) {
    const reportId = event.detail?.reportId
    if (!reportId) return

    // Update internal data
    const report = this._reportsMap[reportId]
    if (report) {
      report.generating = false
    }

    // Update the row: remove spinner, show narration, show photos, make clickable
    const row = this.tableBodyTarget?.querySelector(`tr[data-report-id="${reportId}"]`)
    if (row) {
      const notesCell = row.querySelector("td:nth-child(3) .flex")
      if (notesCell) {
        const narration = report?.narration || ""
        notesCell.innerHTML = `<span class="truncate">${this._escapeHtml(narration)}</span>`
      }
      // Render photo tiles if available
      if (report) {
        const photosCell = row.querySelector("td:nth-child(5)")
        if (photosCell) photosCell.innerHTML = this._photoTiles(report)
      }
      row.classList.remove("cursor-default", "opacity-75")
      row.classList.add("cursor-pointer", "hover:bg-brand/[0.04]")
    }
  }

  _pollGeneratingReport(reportId, pollUrl) {
    let attempts = 0
    const maxAttempts = 40 // ~2 minutes
    const poll = async () => {
      attempts++
      try {
        const csrf = document.querySelector('meta[name="csrf-token"]')?.content
        const resp = await fetch(pollUrl, {
          headers: { "Accept": "application/json", "X-CSRF-Token": csrf },
          credentials: "same-origin"
        })
        const data = await resp.json()
        if (data.processing === 0) {
          const report = this._reportsMap[String(reportId)]
          if (report) {
            if (data.pdf_url) report.pdf_url = data.pdf_url
            if (data.thumbnail_urls) report.thumbnail_urls = data.thumbnail_urls
            if (data.photos_count != null) report.photos_count = data.photos_count
          }
          this._onCustomerReportReady({ detail: { reportId } })
          this._showToast("Customer report is ready.")
          return
        }
        if (attempts >= maxAttempts) {
          this._markGenerationFailed(reportId)
          return
        }
        setTimeout(poll, 3000)
      } catch (e) {
        console.error("[Reports] Poll failed:", e)
        if (attempts >= maxAttempts) {
          this._markGenerationFailed(reportId)
          return
        }
        setTimeout(poll, 5000)
      }
    }
    setTimeout(poll, 3000)
  }

  _markGenerationFailed(reportId) {
    const report = this._reportsMap[reportId]
    if (report) report.generation_failed = true

    const row = this.tableBodyTarget?.querySelector(`tr[data-report-id="${reportId}"]`)
    if (row) {
      const notesCell = row.querySelector("td:nth-child(3) .flex")
      if (notesCell) {
        notesCell.innerHTML = `<span class="text-red-500 truncate">Report generation failed</span>`
      }
      row.classList.remove("cursor-default", "opacity-75")
      row.classList.add("cursor-pointer", "hover:bg-brand/[0.04]")
    }
  }

  // ── Special report events (Progress/KPI) ─────────────────────────

  _onSpecialReportCreated(event) {
    const { report, readyMessage } = event.detail || {}
    if (!report) return

    const id = String(report.id)
    if (readyMessage) report._readyMessage = readyMessage

    // Add to internal data
    this._reportIds.unshift(id)
    this._reportsMap[id] = report

    // Insert row at top of table
    if (this.hasTableBodyTarget) {
      this._rowIndex = 0
      this.tableBodyTarget.insertAdjacentHTML("afterbegin", this._renderRow(report))
      // Re-stripe all rows
      this.tableBodyTarget.querySelectorAll("tr").forEach((tr, i) => {
        tr.classList.remove("bg-gray-50/60", "bg-white")
        tr.classList.add(i % 2 === 1 ? "bg-gray-50/60" : "bg-white")
      })
    }

    // Make sure table view is visible
    if (this.hasTableContainerTarget) this.tableContainerTarget.classList.remove("hidden")
    if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden")

    // Increment breadcrumb count
    const el = document.getElementById("reports-breadcrumb-count")
    if (el) el.textContent = (parseInt(el.textContent) || 0) + 1
  }

  _onSpecialReportReady(event) {
    const reportId = event.detail?.reportId
    if (!reportId) return

    const report = this._reportsMap[reportId]
    if (report) report.generating = false

    const row = this.tableBodyTarget?.querySelector(`tr[data-report-id="${reportId}"]`)
    if (row) {
      const notesCell = row.querySelector("td:nth-child(3) .flex")
      if (notesCell) {
        const narration = report?.narration || ""
        notesCell.innerHTML = `<span class="truncate">${this._escapeHtml(narration)}</span>`
      }
      row.classList.remove("cursor-default", "opacity-75")
      row.classList.add("cursor-pointer", "hover:bg-brand/[0.04]")
    }
  }

  // ── Calendar internals ──────────────────────────────────────────

  async _loadCalendar() {
    if (this._calLoading) return
    this._calLoading = true

    try {
      const url = new URL(this.calendarUrlValue, window.location.origin)
      url.searchParams.set("year", this._calYear)
      url.searchParams.set("month", this._calMonth)

      // Apply active filters
      if (this._filters.type_ids) {
        this._filters.type_ids.forEach(id => url.searchParams.append("type_ids[]", id))
      }
      if (this._filters.search) url.searchParams.set("search", this._filters.search)

      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(url, {
        headers: { "Accept": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      this._calData = data

      this._renderCalendarTitle()
      this._renderCalendarStats(data.stats)
      this._renderCalendarGrid(data)
    } catch (error) {
      console.error("[Reports] Failed to load calendar:", error)
    } finally {
      this._calLoading = false
    }
  }

  _renderCalendarTitle() {
    if (!this.hasCalendarTitleTarget) return
    const date = new Date(this._calYear, this._calMonth - 1, 1)
    this.calendarTitleTarget.textContent = date.toLocaleDateString(
      document.documentElement.lang || "en",
      { month: "long", year: "numeric" }
    )
  }

  _renderCalendarStats(stats) {
    if (!this.hasCalendarStatsTarget) return
    const parts = []
    parts.push(`${stats.total} ${stats.total === 1 ? "report" : "reports"}`)
    parts.push(`${stats.days_with} ${stats.days_with === 1 ? "day" : "days"} with reports`)
    if (stats.days_without > 0) {
      parts.push(`${stats.days_without} without`)
    }
    this.calendarStatsTarget.textContent = parts.join("  ·  ")
  }

  _renderCalendarGrid(data) {
    if (!this.hasCalendarGridTarget) return

    this.closePopover()
    const grid = this.calendarGridTarget
    grid.innerHTML = ""

    const year = data.year
    const month = data.month
    const firstDay = new Date(year, month - 1, 1)
    const lastDay = new Date(year, month, 0)
    const daysInMonth = lastDay.getDate()

    // Monday = 0, Sunday = 6
    let startDow = firstDay.getDay() - 1
    if (startDow < 0) startDow = 6

    const today = new Date()
    const todayStr = this._formatDate(today)
    const isCurrentMonth = today.getFullYear() === year && today.getMonth() + 1 === month

    // Previous month filler cells
    const prevMonthLast = new Date(year, month - 1, 0)
    for (let i = startDow - 1; i >= 0; i--) {
      const d = prevMonthLast.getDate() - i
      grid.insertAdjacentHTML("beforeend", this._calendarCell(d, null, true, false))
    }

    // Current month cells
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`
      const dayData = data.days[dateStr]
      const isToday = dateStr === todayStr
      const isFuture = new Date(dateStr) > today
      grid.insertAdjacentHTML("beforeend", this._calendarCell(d, dateStr, false, isToday, isFuture, dayData))
    }

    // Next month filler cells (complete last row)
    const totalCells = grid.children.length
    const remainder = totalCells % 7
    if (remainder > 0) {
      for (let d = 1; d <= 7 - remainder; d++) {
        grid.insertAdjacentHTML("beforeend", this._calendarCell(d, null, true, false))
      }
    }
  }

  _calendarCell(dayNum, dateStr, isOutside, isToday, isFuture = false, dayData = null) {
    const reportCount = dayData?.reports?.length || 0
    const hasReports = reportCount > 0 && !isOutside
    const thumbnails = (!isOutside && dayData?.thumbnail_urls) || []

    let dayNumClass = "text-[13px] font-medium "
    if (isOutside) {
      dayNumClass += "text-gray-300"
    } else if (isToday) {
      dayNumClass += "text-white"
    } else {
      dayNumClass += "text-gray-700"
    }

    const todayBadge = isToday
      ? `<span class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand text-white text-[13px] font-semibold">${dayNum}</span>`
      : `<span class="${dayNumClass}">${dayNum}</span>`

    // Photo thumbnails (up to 5 + overflow)
    let thumbsHtml = ""
    if (thumbnails.length > 0) {
      const totalPhotos = dayData?.photos_count || thumbnails.length
      const tiles = thumbnails.map(url =>
        `<div class="w-7 h-7 rounded bg-gray-100 overflow-hidden shrink-0">
          <img src="${this._escapeHtml(url)}"
               class="w-full h-full object-cover opacity-0 transition-opacity duration-300"
               loading="lazy"
               data-controller="img-handler"
               data-action="load->img-handler#showLoaded error->img-handler#removeSelf" />
        </div>`
      ).join("")
      const extra = totalPhotos - thumbnails.length
      const overflowTile = extra > 0
        ? `<div class="w-7 h-7 rounded bg-gray-200 shrink-0 flex items-center justify-center">
            <span class="text-[10px] font-medium text-gray-500">+${extra}</span>
          </div>`
        : ""
      thumbsHtml = `<div class="flex flex-wrap gap-[3px] mt-1">${tiles}${overflowTile}</div>`
    }

    // Type color dots
    let dotsHtml = ""
    if (hasReports) {
      const maxDots = 5
      const shown = dayData.reports.slice(0, maxDots)
      const extraReports = reportCount - maxDots
      const dots = shown.map(r => {
        const color = r.is_special ? "#475569" : this._typeColorHex(r.type_name)
        return `<span class="w-5 h-5 rounded-full shrink-0" style="background-color:${color}"></span>`
      }).join("")
      const overflowDot = extraReports > 0
        ? `<span class="w-5 h-5 rounded-full shrink-0 bg-gray-200 flex items-center justify-center">
            <span class="text-[10px] font-medium text-gray-500">+${extraReports}</span>
          </span>`
        : ""
      dotsHtml = `<div class="flex items-center mt-auto pb-0.5">
        <div class="flex flex-wrap gap-1">${dots}${overflowDot}</div>
      </div>`
    }

    // Location icon if any report has geolocation
    const hasGeo = hasReports && dayData.reports.some(r => r.has_geolocation)
    const geoIcon = hasGeo
      ? `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-[20px] h-[20px]"><path fill="rgba(115,193,29,0.85)" d="M12 10.577c.385 0 .742-.09 1.072-.27.33-.179.591-.42.784-.72.192-.302.288-.632.288-.99a1.85 1.85 0 0 0-.288-1 2.076 2.076 0 0 0-.784-.732c-.33-.18-.687-.269-1.072-.269a2.15 2.15 0 0 0-1.835 1 1.85 1.85 0 0 0-.289 1c0 .551.206 1.02.619 1.404.412.385.914.577 1.505.577zM12 3c1.1 0 2.11.25 3.03.75a5.865 5.865 0 0 1 2.166 2.02c.536.858.804 1.8.804 2.826 0 .782-.206 1.68-.619 2.692-.357.86-.852 1.789-1.484 2.789a36.74 36.74 0 0 1-1.794 2.48 30.35 30.35 0 0 1-1.484 1.77L12 19l-.619-.673a30.35 30.35 0 0 1-1.484-1.77 36.74 36.74 0 0 1-1.794-2.48c-.632-1-1.127-1.93-1.484-2.789C6.206 10.276 6 9.378 6 8.596c0-1.025.268-1.968.804-2.827A5.865 5.865 0 0 1 8.97 3.75 6.25 6.25 0 0 1 12 3z"/></svg>`
      : ""

    // Procore icon if any report is from Procore
    const hasProcore = hasReports && dayData.reports.some(r => r.from_procore)
    const procoreCalIcon = hasProcore
      ? `<img src="/procore-icon.png" class="h-[14px] opacity-50" style="width:auto" title="From Procore">`
      : ""

    const bg = isOutside ? "bg-gray-50" : "bg-white"
    const cursor = hasReports ? "cursor-pointer" : ""
    const hover = hasReports ? "hover:bg-brand/[0.04]" : ""
    const action = hasReports ? `data-action="click->reports#calendarDayClick"` : ""
    const dataAttr = dateStr ? `data-date="${dateStr}"` : ""

    return `<div class="relative px-2 pt-1.5 pb-1 border-b border-r border-gray-200 flex flex-col ${bg} ${cursor} ${hover} transition-colors"
                 ${action} ${dataAttr}>
      <div class="self-start">${todayBadge}</div>
      ${thumbsHtml}
      ${dotsHtml}
      ${geoIcon || procoreCalIcon ? `<div class="absolute bottom-1 right-1 flex items-center gap-0.5">${geoIcon}${procoreCalIcon}</div>` : ""}
    </div>`
  }

  _typeColorHex(typeName) {
    const hexColors = [
      "#3B82F6", "#9333EA", "#14B8A6", "#6366F1", "#EC4899", "#06B6D4", "#F97316", "#10B981",
      "#F43F5E", "#8B5CF6", "#F59E0B", "#84CC16", "#0EA5E9", "#D946EF", "#78716C", "#EF4444"
    ]
    if (!typeName) return "#6B7280"
    let hash = 0x811c9dc5
    for (let i = 0; i < typeName.length; i++) {
      hash ^= typeName.charCodeAt(i)
      hash = Math.imul(hash, 0x01000193)
    }
    return hexColors[(hash >>> 0) % hexColors.length]
  }

  _showPopover(cell, dateStr, reports) {
    if (!this.hasCalendarPopoverTarget) return

    this.closePopover()

    // Highlight active cell with persistent hover-like background
    cell.setAttribute("data-active-cell", "true")
    cell.classList.add("!bg-gray-100")

    // Set title
    const date = new Date(dateStr + "T12:00:00")
    if (this.hasPopoverTitleTarget) {
      this.popoverTitleTarget.textContent = date.toLocaleDateString(
        document.documentElement.lang || "en",
        { weekday: "short", month: "short", day: "numeric" }
      )
    }

    // Build report list
    if (this.hasPopoverBodyTarget) {
      this.popoverBodyTarget.innerHTML = reports.map(r => {
        const color = r.is_special ? "#475569" : this._typeColorHex(r.type_name)
        return `<button type="button"
                        class="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-2"
                        data-action="click->reports#popoverOpenReport"
                        data-report-id="${r.id}">
          <span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-semibold whitespace-nowrap bg-white shrink-0"
                style="color:${color};border:1px solid ${color}80;">${this._escapeHtml(r.type_label || r.type_name)}</span>
          <span class="text-sm text-gray-500 truncate">${this._escapeHtml(r.author_name || "")}</span>
        </button>`
      }).join("")
    }

    // Position popover just above the clicked cell (fixed positioning)
    const popover = this.calendarPopoverTarget
    popover.classList.remove("hidden")

    const cellRect = cell.getBoundingClientRect()
    const popRect = popover.getBoundingClientRect()
    const gap = 6

    // Place above the cell, horizontally centered on it
    let top = cellRect.top - popRect.height - gap
    let left = cellRect.left + cellRect.width / 2 - popRect.width / 2

    // If not enough room above, place below the cell
    if (top < 4) {
      top = cellRect.bottom + gap
    }

    // Keep within viewport horizontally
    if (left < 4) left = 4
    if (left + popRect.width > window.innerWidth - 4) left = window.innerWidth - popRect.width - 4

    popover.style.top = `${top}px`
    popover.style.left = `${left}px`

    // Listen for outside clicks (delayed to avoid immediate close)
    requestAnimationFrame(() => {
      document.addEventListener("click", this._onClickOutsidePopover)
    })
  }

  async exportAllReports() {
    const btn = this.hasExportAllBtnTarget ? this.exportAllBtnTarget : null
    if (btn) btn.disabled = true

    try {
      const response = await fetch(`/projects/${this.projectIdValue}/daily_reports/export_all_reports_as_zip`, {
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        }
      })
      if (response.ok) {
        this._showToast("You will receive an email soon with a download link.")
      } else {
        this._showToast("Failed to start export. Please try again.", "error")
      }
    } catch {
      this._showToast("Network error. Please try again.", "error")
    } finally {
      if (btn) btn.disabled = false
    }
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  _onClickOutsidePopover(event) {
    if (!this.hasCalendarPopoverTarget) return
    if (this.calendarPopoverTarget.classList.contains("hidden")) return

    // Don't close if clicking inside popover or on a calendar cell with data
    if (this.calendarPopoverTarget.contains(event.target)) return
    const cell = event.target.closest("[data-date]")
    if (cell && cell.dataset.date) return

    this.closePopover()
  }
}
